import { AlertTriangle, Info, KeyRound, Lightbulb } from "lucide-react";
import { useState } from "react";

import {
  type DeployEnvironment,
  ENVIRONMENT_LABEL,
  ENVIRONMENT_TOKEN,
} from "@/components/app-registration-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  type AppRegistrationTriageRejection,
  type CreateAppRegistrationRequest,
  isApiError,
  type TriageResult,
  useAppRegistrationEnvironments,
  useCreateAppRegistrationRequest,
} from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { ScopePicker } from "./app-registration-scope-picker";

/** Server-side caps, mirrored so the form fails before the round-trip does. */
const MIN_PURPOSE_LENGTH = 10;
const MAX_SCOPES = 25;

/** The OIDC scopes the server injects into every request, shown for honesty.
 *
 * Mirrors `IDENTITY_SCOPE_KEYS` in `services/app_registration_catalog.py`;
 * the server is authoritative and re-adds them regardless of what is sent. */
const ALWAYS_INCLUDED_SCOPES = ["openid", "profile", "email"] as const;

/** What Entra expects when a federated credential doesn't name its own audience. */
const DEFAULT_FEDERATION_AUDIENCE = "api://AzureADTokenExchange";

type CredentialKind = CreateAppRegistrationRequest["credential_kind"];
/** Promotion order — what the picker lists top to bottom. */
const ENVIRONMENT_ORDER: DeployEnvironment[] = ["dev", "test", "prod"];

interface AppRegistrationRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  projectName: string;
}

/**
 * Ask for an Entra app registration for a project.
 *
 * The form collects only what the requester actually knows. The display name
 * (`NO-AR-<Project>`) and the requester's email are derived server-side and
 * are deliberately not sent — the backend ignores them if they are.
 */
export function AppRegistrationRequestDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
}: AppRegistrationRequestDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Request an app registration</DialogTitle>
          <DialogDescription>
            For <span className="font-medium">{projectName}</span>. An admin reviews the request
            before anything is created in Entra.
          </DialogDescription>
        </DialogHeader>
        {open && (
          // Remount per opening so every field starts clean — same idiom as
          // InviteMemberDialog, and it avoids syncing state in an effect.
          <AppRegistrationRequestForm
            key={projectId}
            projectId={projectId}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AppRegistrationRequestForm({
  projectId,
  onClose,
}: {
  projectId: number;
  onClose: () => void;
}) {
  const createRequest = useCreateAppRegistrationRequest(projectId);

  const environments = useAppRegistrationEnvironments(projectId);

  const [purpose, setPurpose] = useState("");
  // An app registration belongs to exactly one environment. Deliberately not
  // defaulted: `NO-AR-<Project>-DEV` and `-PRD` are different applications
  // with different consent, so a pre-selected value is a wrong answer nobody
  // reads. `validate()` refuses an empty one on submit.
  const [environment, setEnvironment] = useState<DeployEnvironment | "">("");
  const [actsAsSignedInUser, setActsAsSignedInUser] = useState(false);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [error, setError] = useState("");
  // A 409 is not a failure — it is the server's triage verdict that this
  // workload wants a managed identity instead. Held separately so it renders
  // as an explanation rather than a red banner.
  const [triage, setTriage] = useState<TriageResult | null>(null);

  const submitting = createRequest.isPending;

  const toggleScope = (key: string) => {
    setSelectedScopes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const validate = (): string => {
    if (!environment) {
      return "Choose which environment this registration is for.";
    }
    if (purpose.trim().length < MIN_PURPOSE_LENGTH) {
      return `Describe what the app is for — at least ${MIN_PURPOSE_LENGTH} characters.`;
    }
    if (selectedScopes.length === 0) {
      return "Select at least one permission — the sign-in scopes are added for you and reach nothing on their own.";
    }
    if (selectedScopes.length > MAX_SCOPES) {
      return `Select at most ${MAX_SCOPES} permissions. Ask for a second registration if you genuinely need more.`;
    }
    return "";
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }
    setError("");
    setTriage(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const body: CreateAppRegistrationRequest = {
      purpose: purpose.trim(),
      scope_keys: selectedScopes,
      environment: environment as DeployEnvironment,
      acts_as_signed_in_user: actsAsSignedInUser,
    };

    try {
      await createRequest.mutateAsync(body);
      onClose();
    } catch (err) {
      const verdict = triageVerdictFrom(err);
      if (verdict) {
        logger.debug("[app-registration] triage verdict:", verdict.verdict);
        setTriage(verdict);
        return;
      }
      logger.error("[app-registration] request failed:", err);
      // The other 409 is a `PlainErrorResponse` — a sentence in `detail`, e.g.
      // the environment already has this project's sign-in registration —
      // which `readApiErrorMessage` shows as is. Re-read the environments too,
      // so the picker stops offering the one that was just refused.
      if (isApiError(err) && err.status === 409) {
        void environments.refetch();
      }
      setError(readApiErrorMessage(err, "Failed to submit the request. Please try again."));
    }
  };

  if (triage) {
    return <TriageVerdict triage={triage} onReconsider={() => setTriage(null)} onClose={onClose} />;
  }

  return (
    <>
      <DialogBody>
        <div className="space-y-6">
          <EnvironmentPicker
            selected={environment}
            onSelect={setEnvironment}
            available={environments.data?.available ?? []}
            taken={environments.data?.taken ?? []}
            signIn={environments.data?.sign_in ?? []}
            loading={environments.isLoading}
            failed={Boolean(environments.error)}
            disabled={submitting}
          />

          <div className="space-y-1.5">
            <Label htmlFor="app-reg-purpose">What is the app for?</Label>
            <Textarea
              id="app-reg-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. A scheduled job that reads the requesting user's Outlook calendar to build the team's capacity report."
              disabled={submitting}
              rows={3}
            />
            <p className="text-muted-foreground text-xs">
              The approver reads this and nothing else about your workload — be concrete.
            </p>
          </div>

          <fieldset className="space-y-3" disabled={submitting}>
            <legend className="text-sm font-medium">How it uses identity</legend>
            <p className="text-muted-foreground text-xs">
              This answer decides whether an app registration is the right tool at all. The server
              re-runs the check on submit.
            </p>
            <ToggleRow
              id="app-reg-acts-as-user"
              label="A person signs in, and it then acts as that person"
              hint="It calls APIs on their behalf and sees only what they can see — not a shared service account view. Leave it off if the workload calls APIs as itself: that is your project's managed identity, and it needs nothing from here."
              checked={actsAsSignedInUser}
              onCheckedChange={setActsAsSignedInUser}
              disabled={submitting}
            />
          </fieldset>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="app-reg-scopes">Delegated permissions</Label>
              <span className="text-muted-foreground text-xs">
                {selectedScopes.length} of {MAX_SCOPES} selected
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              Every permission here acts as the signed-in user and can never reach data that user
              cannot already reach. Ask for the narrowest set that works.
            </p>
            {/* Stated rather than offered. Sign-in needs these three and they
                reach nothing, so choosing them was never a real choice -- and
                leaving them to be chosen is exactly what produced a
                registration that was consented and then could not make a
                single call. */}
            <div className="bg-muted/40 flex flex-wrap items-center gap-1.5 rounded-md border px-3 py-2">
              <span className="text-muted-foreground text-xs">Always included:</span>
              {ALWAYS_INCLUDED_SCOPES.map((value) => (
                <Badge key={value} variant="secondary" className="font-mono text-xs">
                  {value}
                </Badge>
              ))}
              <span className="text-muted-foreground text-xs">
                &mdash; who signed in, nothing more. Sign-in fails without them.
              </span>
            </div>
            <ScopePicker
              selected={selectedScopes}
              onToggle={toggleScope}
              disabled={submitting}
              atLimit={selectedScopes.length >= MAX_SCOPES}
            />
          </div>

          {/* No credential controls: there is exactly one kind, and its
              subject is resolved from this project rather than typed. Showing
              a field whose only valid value is derived would be a decision
              the requester cannot actually make. */}
          <div className="text-muted-foreground rounded-lg border border-dashed p-3 text-xs">
            <p>
              <span className="text-foreground font-medium">Credential:</span> a federated
              credential on this project&apos;s own managed identity. No client secret is created —
              there is nothing to store, rotate or leak.
            </p>
            <p className="mt-1.5">
              Your app&apos;s callbacks are registered for you on approval, so it can ask Microsoft
              for these permissions as the person using it. Signing in to the app itself is
              unchanged.
            </p>
          </div>

          {error && (
            <p className="text-destructive flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>
      </DialogBody>

      <DialogFooter className="px-6 pb-6">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting…" : "Submit request"}
        </Button>
      </DialogFooter>
    </>
  );
}

function EnvironmentPicker({
  selected,
  onSelect,
  available,
  taken,
  signIn,
  loading,
  failed,
  disabled,
}: {
  selected: DeployEnvironment | "";
  onSelect: (environment: DeployEnvironment) => void;
  available: DeployEnvironment[];
  taken: DeployEnvironment[];
  /** The subset of `taken` held by the project's own sign-in registration. */
  signIn: DeployEnvironment[];
  loading: boolean;
  failed: boolean;
  disabled: boolean;
}) {
  // Taken environments are rendered disabled rather than omitted. One that
  // silently vanishes from a three-item list reads as a bug, and the unique
  // index would refuse the insert anyway — better to say why here than to
  // return a 409 nobody expected.
  //
  // `sign_in` is documented as a subset of `taken`; the union is taken anyway
  // so an environment held by the sign-in registration is never offered, even
  // if the two lists ever disagree. Its reason names the way forward: the
  // permissions go onto that same registration.
  const blocked = [...new Set([...taken, ...signIn])];
  const rows: { environment: DeployEnvironment; reason: string | null }[] = [
    ...available
      .filter((environment) => !blocked.includes(environment))
      .map((environment) => ({ environment, reason: null })),
    ...blocked.map((environment) => ({
      environment,
      reason: signIn.includes(environment)
        ? `${ENVIRONMENT_TOKEN[environment]} already has this project's sign-in registration — use Change permissions on it to add Microsoft Graph permissions.`
        : "Already has a registration or a request in flight.",
    })),
  ].sort(
    (a, b) => ENVIRONMENT_ORDER.indexOf(a.environment) - ENVIRONMENT_ORDER.indexOf(b.environment),
  );

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="text-sm font-medium">Which environment?</legend>
      <p className="text-muted-foreground text-xs">
        One registration serves one environment, so each gets its own application, its own consent
        and its own client id. Promoting the project later means requesting another.
      </p>

      {loading ? (
        <p className="text-muted-foreground text-xs">Checking what this project has deployed…</p>
      ) : failed ? (
        // Never an empty list on a failed read: "this project has no
        // environments" is a claim the UI cannot make when it never managed
        // to ask.
        <p className="text-destructive text-xs">
          Could not read this project&apos;s environments. Reload and try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          Nothing is deployed yet. An app registration needs a deployed environment to register a
          callback against and an identity to federate — deploy first, then come back.
        </p>
      ) : (
        <div className="space-y-2" role="radiogroup" aria-label="Environment">
          {rows.map(({ environment, reason }) => (
            <button
              key={environment}
              type="button"
              // `aria-disabled`, not `disabled`. A disabled button leaves the
              // tab order and most screen readers skip it -- which would hide
              // the reason text that is the entire justification for rendering
              // a taken environment rather than omitting it. The click is
              // neutralised instead, so the row stays reachable and readable.
              onClick={() => reason === null && onSelect(environment)}
              aria-disabled={disabled || reason !== null}
              // Single-select, so radio rather than the toggle-button pattern:
              // `aria-pressed` announces "pressed / not pressed" and implies
              // the choice can be un-pressed, which `onSelect` never does.
              role="radio"
              aria-checked={selected === environment}
              aria-describedby={reason ? `app-reg-env-${environment}-reason` : undefined}
              className={cn(
                "flex w-full items-start justify-between gap-4 rounded-lg border px-3 py-2.5 text-left",
                "aria-disabled:cursor-not-allowed aria-disabled:opacity-60",
                selected === environment && "border-primary ring-primary/30 ring-2",
              )}
            >
              <div className="space-y-0.5">
                <span className="text-sm font-medium">{ENVIRONMENT_LABEL[environment]}</span>
                <p
                  id={reason ? `app-reg-env-${environment}-reason` : undefined}
                  className="text-muted-foreground text-xs"
                >
                  {reason ?? `Creates NO-AR-<project>-${ENVIRONMENT_TOKEN[environment]}`}
                </p>
              </div>
              <Badge variant={reason ? "outline" : "secondary"}>
                {ENVIRONMENT_TOKEN[environment]}
              </Badge>
            </button>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function ToggleRow({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border px-3 py-2.5">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="mt-1"
      />
    </div>
  );
}

/**
 * The 409 branch: the request was well-formed, the premise was wrong.
 *
 * Rendered as an answer to the question the requester actually asked — "what
 * should this workload use?" — with the verdict's own reasons. Going back is
 * offered because the two identity answers are the only inputs to the verdict,
 * so a mis-click is the likeliest cause of landing here.
 */
function TriageVerdict({
  triage,
  onReconsider,
  onClose,
}: {
  triage: TriageResult;
  onReconsider: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <DialogBody>
        <div className="space-y-4 rounded-lg border border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/40">
          <div className="flex items-start gap-3">
            <Lightbulb className="mt-0.5 size-5 shrink-0 text-sky-600 dark:text-sky-400" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-sky-900 dark:text-sky-100">
                {triage.headline}
              </p>
              <p className="text-xs text-sky-800 dark:text-sky-300">
                Nothing was submitted — this is a recommendation, not an error.
              </p>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-sky-900 dark:text-sky-100">
            {triage.reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-2">
                <Info className="mt-0.5 size-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 rounded-md bg-sky-100 px-3 py-2 dark:bg-sky-900/50">
            <KeyRound className="size-4 shrink-0 text-sky-700 dark:text-sky-300" />
            <span className="text-sm text-sky-900 dark:text-sky-100">
              Use <span className="font-mono font-medium">{triage.recommended_name}</span>
            </span>
          </div>
        </div>
      </DialogBody>

      <DialogFooter className="px-6 pb-6">
        <Button variant="outline" onClick={onReconsider}>
          Back to the form
        </Button>
        <Button onClick={onClose}>Got it</Button>
      </DialogFooter>
    </>
  );
}

/**
 * Pull the triage verdict out of a 409, or `null` for any other failure.
 *
 * The 409 body is declared in the schema, so the *shape* comes from the
 * generated `AppRegistrationTriageRejection` — but `ApiError.body` is
 * `unknown` (the error interceptor cannot know which route threw), so it still
 * has to be checked at runtime. Anything that isn't a recognisable verdict
 * falls through to the ordinary error path rather than rendering a half-empty
 * explanation.
 */
function triageVerdictFrom(error: unknown): TriageResult | null {
  if (!isApiError(error) || error.status !== 409) {
    return null;
  }
  const detail = (error.body as { detail?: unknown } | undefined)?.detail;
  if (typeof detail !== "object" || detail === null) {
    return null;
  }
  const { triage } = detail as Partial<AppRegistrationTriageRejection>;
  if (!triage || typeof triage.headline !== "string" || !Array.isArray(triage.reasons)) {
    return null;
  }
  return triage;
}
